<script setup lang="ts">
import { PlusIcon } from "@heroicons/vue/24/outline";
import { useQueryClient } from "@tanstack/vue-query";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { toast } from "vue-sonner";

import { postReceipt } from "@/api";
import { compressImage } from "@/utils/compressImage";

import TrashIcon from "@/components/icons/TrashIcon.vue";
import Button from "@/components/ui/Button.vue";
import Spinner from "@/components/ui/Spinner.vue";

const MAX_PHOTOS = 4;

const { t } = useI18n();
const router = useRouter();
const queryClient = useQueryClient();

const blobs = defineModel<string[]>("blobs", { required: true });
const files = defineModel<File[]>("files", { required: true });

const fileInput = ref<HTMLInputElement | null>(null);
const activePhotoIndex = ref(0);
const isRetaking = ref(false);
const isLoading = ref(false);

const activePhotoBlob = computed(() => blobs.value[activePhotoIndex.value]);
const canAddMorePhotos = computed(() => blobs.value.length < MAX_PHOTOS);

function handleTakePhoto() {
  if (!fileInput.value || !canAddMorePhotos.value) {
    return;
  }

  isRetaking.value = false;
  fileInput.value.click();
}

function handleRetakePhoto() {
  if (!fileInput.value || !canAddMorePhotos.value) {
    return;
  }

  isRetaking.value = true;
  fileInput.value.click();
}

async function handlePhotoCapture(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input?.files?.[0];

  if (!file) {
    return;
  }

  // Compress the image immediately after capture
  const compressedFile = await compressImage(file);

  const newPhotoUrl = URL.createObjectURL(compressedFile);

  if (isRetaking.value) {
    // Revoke unused image to prevent memory leaks.
    if (blobs.value[activePhotoIndex.value]) {
      URL.revokeObjectURL(blobs.value[activePhotoIndex.value]);
    }

    blobs.value[activePhotoIndex.value] = newPhotoUrl;
    files.value[activePhotoIndex.value] = compressedFile;
  } else {
    blobs.value.push(newPhotoUrl);
    files.value.push(compressedFile);

    activePhotoIndex.value = blobs.value.length - 1;
  }

  // Reset input value to allow selecting the same file again
  if (fileInput.value) {
    fileInput.value.value = "";
  }
}

function handleClickPhoto(index: number) {
  // If currently on the active photo, ensure there are at least 2 photo and remove it
  if (activePhotoIndex.value === index && blobs.value.length > 1) {
    blobs.value.splice(index, 1);
    files.value.splice(index, 1);
    activePhotoIndex.value = 0;
  } else {
    activePhotoIndex.value = index;
  }
}

async function handleSubmitReceipt() {
  if (!files.value.length || isLoading.value) {
    return;
  }

  isLoading.value = true;

  try {
    await postReceipt([...files.value]);

    queryClient.invalidateQueries({ queryKey: ["receipts"] });
    router.push({ name: "receipts" });
    toast(t("toast.submitReceiptSuccess"), { position: "top-center" });
  } finally {
    isLoading.value = false;
  }
}
</script>

<template>
  <div
    class="flex h-svh w-full flex-col justify-end"
    role="region"
    aria-label="Capture et prévisualisation des photos"
  >
    <div class="relative flex-auto">
      <img
        :src="activePhotoBlob"
        :alt="`Photo ${activePhotoIndex + 1} sur ${blobs.length}`"
        class="pointer-events-none absolute inset-0 h-full w-full object-cover object-center"
      />

      <button
        v-if="canAddMorePhotos"
        type="button"
        class="bg-galbani absolute bottom-8 left-1/2 flex size-14 -translate-x-1/2 cursor-pointer items-center justify-center rounded-full text-white"
        :aria-label="`Ajouter une photo (${blobs.length}/${MAX_PHOTOS})`"
        @click="handleTakePhoto"
      >
        <PlusIcon class="size-8" aria-hidden="true" />
      </button>
    </div>

    <div
      class="relative flex w-full flex-col gap-2 bg-black px-6 py-4"
      role="toolbar"
      aria-label="Gestion des photos"
    >
      <div
        v-if="blobs.length > 1"
        class="flex items-center justify-center gap-4"
        role="tablist"
        aria-label="Navigation entre les photos"
      >
        <button
          v-for="(picture, index) in blobs"
          :key="picture"
          class="relative size-12 cursor-pointer rounded-lg border-3"
          :class="{
            'border-topchef': activePhotoIndex === index,
          }"
          role="tab"
          :aria-selected="activePhotoIndex === index"
          :aria-label="`Voir photo ${index + 1}`"
          @click="handleClickPhoto(index)"
        >
          <span
            v-if="activePhotoIndex === index"
            class="pointer-events-none absolute inset-0 rounded bg-black/50"
          />

          <img
            :src="picture"
            :alt="`Miniature photo ${index + 1}`"
            class="block aspect-square h-full w-full rounded object-cover object-center"
          />

          <TrashIcon
            v-if="activePhotoIndex === index"
            class="pointer-events-none absolute top-1/2 left-1/2 size-7 -translate-x-1/2 -translate-y-1/2 text-white"
          />
        </button>
      </div>

      <div class="grid grid-cols-2 gap-4" role="group" aria-label="Actions">
        <Button
          variant="primary"
          aria-label="Envoyer toutes les photos"
          @click="handleSubmitReceipt"
        >
          <template v-if="!isLoading">{{ $t("pictureUploader.submitButton") }}</template>

          <Spinner v-else class="size-7" />
        </Button>

        <Button
          variant="outline"
          :aria-label="`Reprendre la photo ${activePhotoIndex + 1}`"
          @click="handleRetakePhoto"
        >
          {{ $t("pictureUploader.retakeButton") }}
        </Button>
      </div>
    </div>

    <input
      ref="fileInput"
      type="file"
      accept="image/*"
      capture="environment"
      class="hidden"
      aria-label="Prendre une photo avec l'appareil photo"
      @change="handlePhotoCapture"
    />
  </div>
</template>
